import { PSBErrors } from "@/constants/errors/product-slot-booking";
import BookingModule from "@/store/modules/booking";
import PSSModule from "@/store/modules/pss";
import { AccountingState } from "@/types/db/accounting";
import {
  IPackagePrice,
  IProductCategory,
  IProductSlotPrice,
  MEMBERSHIP_TICKETS,
  PCTypes,
} from "@/types/db/product-category";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  ICreateAddons,
  ICreatePSS,
  IProductSlotState,
  PaymentStatus,
  PSSType,
} from "@/types/db/product-slot-state";
import { ITimeSlot } from "@/types/db/timeslots";
import { getProductCategoryByPriceId } from "@/dexie/product-category";
import { queryTimeslotsByProductCategories } from "@/dexie/timeslots";
import { getFutureTimeSlotsByProductCategory2 } from "../helpers/timeslots";
import { getPcStockUnits } from "../helpers/stock";
import dayjs from "dayjs";
import QRCode from "qrcode";
import { PSSErrors } from "@/constants/errors/product-slot-state";
import { getClosestDate } from "../helpers/dates";

/**
 * Whether or not a pss is fully paid
 */
export function isPssPaid(pss: IProductSlotState): boolean {
  if (
    /*pss.bookingStatus === BookingStatus.Confirmed || In B2C, partially paid pss are Confirmed*/
    pss.paymentStatus === PaymentStatus.Completed ||
    (hasAccEntry(pss) && getBalance(pss) >= 0)
  )
    return true;
  return false;
}

/**
 * Whether or not a given pss is mentioned in any of the completed accountings
 */
export function hasAccEntry(pss: IProductSlotState): boolean {
  const booking = BookingModule.selectedBooking;
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  const pssAccEntries = booking.accountings
    .filter((acc) => acc.state === AccountingState.Completed)
    .map((acc) => acc.accountingEntriesList)
    .flat()
    .filter((accEntry) => accEntry.productSlotStateUuid === pss.uuid);
  if (pssAccEntries && pssAccEntries.length) {
    return true;
  }
  return false;
}

/**
 * returns the balance of a given pss based on completed accountings
 */
export function getBalance(pss: IProductSlotState): number {
  let balance = 0;
  const booking = BookingModule.selectedBooking;
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const pssAccEntries = booking.accountings
    .filter((acc) => acc.state === AccountingState.Completed)
    .map((acc) => acc.accountingEntriesList)
    .flat()
    .filter((accEntry) => accEntry.productSlotStateUuid === pss.uuid);

  pssAccEntries.forEach((ae) => (balance = balance + ae.value));
  return balance;
}

export function getAutomaticAddonQuantityInBooking(
  addonId: number,
  booking: IProductSlotBooking
) {
  let quantity = 0;
  for (let i = 0; i < booking.pssList.length; i++) {
    const pss = booking.pssList[i];
    if (!pss.isAutomaticAddon || pss.packagePriceId !== addonId) {
      continue;
    }
    quantity++;
  }
  return quantity;
}

/**
 * Giftcards have their own validity timeslot, independent of any parent ticket's time.
 * Fetch it directly instead of filtering by a window around the main ticket start.
 * Mirrors how the standalone main-ticket path picks the giftcard timeslot.
 */
export async function getGiftCardAddonTimeslot(
  addonPC: IProductCategory
): Promise<ITimeSlot | undefined> {
  const timeslots = await queryTimeslotsByProductCategories({
    categoryIds: [addonPC.id],
    endDateTimeAfter: dayjs().toISOString(),
  });
  return timeslots?.[0];
}

/**
 * returns an array of type ICreateAddons with all automatic addons related to the given price
 * @param price: IPSP
 * @param booking: IPSB
 */
export async function getPriceAutomaticAddons(
  price: IProductSlotPrice,
  booking: IProductSlotBooking,
  startDateTime?: string | undefined,
  maintimeSlotId?: string
): Promise<ICreateAddons[]> {
  const pssAddons: ICreateAddons[] = [];
  for (const addon of price.addons) {
    if (addon.canceled === 1 || addon.automaticallyBook !== 1) {
      continue;
    }
    const addonPC = await getProductCategoryByPriceId(addon.priceId);
    if (!addonPC) {
      // console.log("addon: PC not found");
      continue;
    }

    /**
     * if addon has a max quantity constraints and addons with same package price id already cover it, then we do not add the automatic addon
     */
    const addonMaxQuantity = addon.maxQuantity;
    let currentQuantity = 0;
    for (let i = 0; i < pssAddons.length; i++) {
      const pssAddon = pssAddons[i];
      if (!pssAddon.isAutomaticAddon || pssAddon.packagePriceId !== addon.id) {
        continue;
      }
      currentQuantity++;
    }
    const addonQuantityInBooking = getAutomaticAddonQuantityInBooking(
      addon.id,
      booking
    );
    currentQuantity += addonQuantityInBooking;
    if (currentQuantity >= addonMaxQuantity) {
      continue;
    }

    const isRetail = addonPC.type === PCTypes[PCTypes.Retail];
    let closestTimeslot: ITimeSlot | undefined;
    if (!isRetail) {
      const mainTicketStartDateTime = startDateTime
        ? dayjs(startDateTime).utc()
        : dayjs().utc();

      if (addonPC.isRecurring) {
        // check for minutes after and minutes before to determine the startDateTime of the addon timeslot

        const minAfter = addon.minutesAfter || 0;
        const minBefore = addon.minutesBefore || 0;
        const from = mainTicketStartDateTime
          .subtract(minBefore, "minute")
          .toISOString();
        const to = mainTicketStartDateTime
          .add(minAfter, "minute")
          .toISOString();

        const timeslots = await getFutureTimeSlotsByProductCategory2(
          Number(addonPC.id),
          from,
          to
        );
        if (timeslots?.length) {
          const closestDate = getClosestDate(
            timeslots.map((ts) => dayjs(ts.startDateTime)),
            mainTicketStartDateTime
          );
          closestTimeslot = timeslots.find((ts) =>
            dayjs(ts.startDateTime).isSame(closestDate)
          );
        }
      } else {
        // Non-recurring addons
        // pick the earliest available one.
        const addonTimeslots = await queryTimeslotsByProductCategories({
          categoryIds: [Number(addonPC.id)],
          endDateTimeAfter: mainTicketStartDateTime.toISOString(),
        });
        closestTimeslot = addonTimeslots.find((ts) => ts.availability >= 1);
      }

      if (!closestTimeslot) {
        if (addon.isMandatory) {
          const error = {
            message: PSSErrors.UnavailableMandatoryAddon,
            addon: addonPC.name,
          };
          throw error;
        }
        continue;
      }
    }
    const sendTicket =
      addon.automaticallyBook === 1 &&
      addonPC.type === PCTypes[PCTypes.GiftCard]
        ? 0
        : addon.sendTicket;
    pssAddons.push({
      automaticallyBook: addon.automaticallyBook,
      isMandatory: addon.isMandatory,
      pssType: isRetail ? PSSType[PSSType.Retail] : PSSType[PSSType.Ticket],
      bookingUuid: booking.uuid as string,
      priceId: addon.priceId,
      categoryId: addonPC.id,
      priceValue: addon.displayedValue || 0,
      accountingValue: addon.accountingValue ?? undefined,
      timeslot: closestTimeslot,
      productCategory: addonPC,
      count: 1,
      // create booking will throw if we have no seller id
      sellerId: booking.sellerId as number,
      sendTicket,
      token: null,
      isAutomaticAddon: addon.automaticallyBook === 1,
      mainSlot: !isRetail ? `${maintimeSlotId}:${price.id}` : null,
      packagePriceId: addon.id,
      tokenEndDate: closestTimeslot?.endDateTime,
      bookingReference: booking.reference,
      maxQuantity: addon.maxQuantity,
    });
  }

  return pssAddons;
}

/**
 * returns an array of type IPackagePrice with non-automatic addons related to the given price
 * @param price: IPSP
 */
export async function getPriceNonAutomaticAddons(
  price: IProductSlotPrice | null
): Promise<IPackagePrice[]> {
  const pssAddons: IPackagePrice[] = [];
  if (!price) {
    return pssAddons;
  }
  for (const addon of price.addons) {
    if (addon.canceled === 1 || addon.automaticallyBook === 1) {
      continue;
    }
    const addonPC = await getProductCategoryByPriceId(addon.priceId);
    if (!addonPC) {
      console.log("addon: PC not found", addon.priceId);
      continue;
    }
    pssAddons.push(addon);
  }
  return pssAddons;
}

/**
 * Takes a list of prices (and timeslot if available) and returns how many pss we have from them in the current booking
 * If the card data supplied corresponds to a PC XXX that is linked to the same Stock entity as a given PSS PC YYY of the booking
 * then we consider this pss as if it was booked from PC XXX
 *
 * 1- PC of PSS and PC of card share the same stock:
 * 1.A - If there is timeslot data and pss timeslot and card timeslot happen in the same time: we consider the pss in counting
 * 1.B - If there is tomeslot data and pss timeslot and card timeslot don't share the same period, we don't ount the pss
 * 1.C - If there is no timeslot data, we count the pss
 * 2- PSS and card don't share the same stock:
 * 2.A- If they are related to the timeslotID, we count the pss
 * 2.B- Else, if they share the same price, we count the pss
 *
 * Counting happens in stock units (weighs its real consumption, L3-7946)
 * and converts back to tickets of the card's category, so callers keep
 * subtracting the result from their availability.
 */
export function countPssByPricesAndTimeslots(
  prices: IProductSlotPrice[],
  productCategoryId: number,
  timeSlotData?: {
    timeslotId: string;
    startDateTime: string;
    endDateTime: string;
  },
  ownStockUnits = 1
): number {
  const booking = BookingModule.selectedBooking;
  if (!booking) {
    return 0;
  }
  const ownUnits = Math.max(1, ownStockUnits);
  let count = 0;
  // Only pss that hasn't been taken into consideration in local stock AND not coming from backend
  // are considered here

  const pssList = booking.pssList.filter(
    (pss) => !pss.id && !pss.isReflectedInLocalAvailability
  );
  const pricesIds = prices?.map((p) => p.id);
  for (let i = 0; i < pssList.length; i++) {
    // If the pss is related to the timeslot or one of the prices supplied then we consider this pss
    // OR, if the pss belong to a category that is linked to a stock entity same as the PC mentioned in Card
    // then we count it as well
    const pssExtension = PSSModule.pssExtensions[String(pssList[i].uuid)];
    // each pss weighs the stock units its category consumes
    const pssUnits = Math.max(1, pssList[i].stockUnits || 1);
    if (
      pssExtension &&
      pssExtension.sameStockPCs &&
      pssExtension.sameStockPCs.includes(productCategoryId)
    ) {
      // Lets check if we have timslot supplied then we need to consider it and do a check with it
      if (timeSlotData) {
        // we have timeslot supplied, lets check if the time ranges overlap
        if (
          pssList[i].startDateTime &&
          pssList[i].endDateTime &&
          dayjs(pssList[i].startDateTime).isBefore(timeSlotData.endDateTime) &&
          dayjs(pssList[i].endDateTime).isAfter(timeSlotData.startDateTime)
        ) {
          count = count + pssUnits;
        }
        continue;
      } else {
        // no timeslotId supplied, we need to increase the count
        count = count + pssUnits;
      }
      // So this is the case of categories linked to the same stock
    } else if (timeSlotData) {
      if (
        timeSlotData.timeslotId &&
        pssList[i].timeSlotId === Number(timeSlotData.timeslotId)
      ) {
        // same timeslot means same category as the card
        count = count + ownUnits;
      }
    } else if (pricesIds?.includes(pssList[i].priceId)) {
      // So no timeslot supplied, we're checking the price
      count = count + ownUnits;
    }
  }
  // convert stock units back to tickets of the card's category
  return Math.ceil(count / ownUnits);
}

/**
 * returns the availability of a given pss or addon
 * @param pssToCreate: ICreateAddons | ICreatePSS
 */
export function getPssAvailability(
  pssToCreate: ICreateAddons | ICreatePSS
): number {
  const { pssType, timeslot, productCategory, priceId } = pssToCreate;
  const ownStockUnits = getPcStockUnits(productCategory.stockConsumptions);
  let bookedAlready = 0;
  if (!pssToCreate.timeslot) {
    bookedAlready = countPssByPricesAndTimeslots(
      pssToCreate.productCategory.prices,
      pssToCreate.productCategory.id,
      undefined,
      ownStockUnits
    );
  } else {
    bookedAlready = countPssByPricesAndTimeslots(
      pssToCreate.productCategory.prices,
      pssToCreate.productCategory.id,
      {
        timeslotId: String(pssToCreate.timeslot.id),
        startDateTime: String(pssToCreate.timeslot.startDateTime),
        endDateTime: String(pssToCreate.timeslot.endDateTime),
      },
      ownStockUnits
    );
  }
  // if the product is a ticket then we consider the availability of the timeslot
  // otherwise we consider psp.quantity and SellOnSoldOut option of the corresponding price
  let availability = 0;
  availability =
    pssType === PSSType[PSSType.Ticket]
      ? timeslot?.availability || 0
      : productCategory.prices.find((p) => p.id === priceId)?.quantity || 0;
  return availability - bookedAlready;
}

/**
 * checks if a given pss is a membership
 * @param pss IPSS
 */
export function isMembershipTicket(pss: IProductSlotState): boolean {
  if (MEMBERSHIP_TICKETS.includes(pss.type) || pss.isMemberhsipSeated) {
    return true;
  }
  return false;
}

export async function getTicketQRCode(
  pss: IProductSlotState,
  voucherCode?: string
) {
  const isVoucherType =
    pss.type === PCTypes.MembershipVoucher || pss.type === PCTypes.GiftCard;
  let qrcodeString = pss.token || "";
  if (isVoucherType) {
    qrcodeString = JSON.stringify({
      token: pss.token,
      code: voucherCode,
    });
  }
  return await QRCode.toDataURL(qrcodeString, { margin: 0 });
}
